import { supabase } from '@/lib/supabase';
import { logger } from '@/utils/logger';

export interface EventPublicStats {
  volunteerCount: number;
  totalHours: number;
}

/** Keep in sync with the cardinality guard in get_events_public_stats. */
const MAX_EVENT_IDS_PER_CALL = 200;

interface StatsRow {
  event_id: string;
  // SUM()/COUNT() come back as bigint, which PostgREST may serialise as a string.
  volunteer_count: number | string | null;
  total_hours: number | string | null;
}

/**
 * Aggregate volunteer stats for events the caller may not be a member of.
 *
 * RLS on `volunteers` only exposes rows to the organizing org, the volunteer themselves,
 * and members of the event — so counting client-side silently yields 0 for everyone else
 * (an empty result set, not an error). `get_events_public_stats` is SECURITY DEFINER and
 * returns aggregates only. See supabase/migrations/20260831120000_event_public_stats.sql.
 *
 * Events missing from the returned map have unknown stats — the call failed for them.
 * That is deliberately distinct from a present entry reading 0, so callers can avoid
 * rendering "0 volunteers" for what is really a failed read.
 */
export async function fetchEventPublicStats(
  eventIds: string[]
): Promise<Record<string, EventPublicStats>> {
  const byEventId: Record<string, EventPublicStats> = {};
  const uniqueIds = [...new Set(eventIds.filter(Boolean))];
  if (uniqueIds.length === 0) return byEventId;

  for (let i = 0; i < uniqueIds.length; i += MAX_EVENT_IDS_PER_CALL) {
    const chunk = uniqueIds.slice(i, i + MAX_EVENT_IDS_PER_CALL);
    const { data, error } = await supabase.rpc('get_events_public_stats', {
      p_event_ids: chunk,
    });
    if (error) {
      logger.error('Error fetching event public stats:', error);
      continue;
    }
    for (const row of (data ?? []) as StatsRow[]) {
      if (!row?.event_id) continue;
      byEventId[row.event_id] = {
        volunteerCount: Number(row.volunteer_count ?? 0),
        totalHours: Number(row.total_hours ?? 0),
      };
    }
  }

  return byEventId;
}
